import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import LoadingSpinner from '../components/LoadingSpinner';
import Comments from '../components/Comments';
import { FishImage } from '../components/GameImages';
// import './Page.css';
import '../css.css'

const Page1 = () => {
    const [fishesData, setFishesData] = useState([]);
    const [filteredData, setFilteredData] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [loading, setLoading] = useState(true);
    const [isHeaderFixed, setIsHeaderFixed] = useState(false);
    
    const tableRef = useRef(null);
    const headerRef = useRef(null);
    const cloneHeaderRef = useRef(null);

    // 이미지를 blob으로 캐싱하는 함수 (최적화된 버전) - 사용하지 않음, 제거 예정
    // const loadImageAsBlob = useCallback(async (filename) => {
    //     // 간단한 이미지 URL 반환 (브라우저 캐싱 활용)
    //     if (!filename) return null;
    //     return `/data/img/fishes/${filename}`;
    // }, []);

    // 스크롤 이벤트 핸들러
    useEffect(() => {
        const handleScroll = () => {
            if (!tableRef.current || !headerRef.current) return;

            const tableRect = tableRef.current.getBoundingClientRect();
            const headerHeight = 65; // 페이지 헤더 높이

            // 테이블이 헤더 아래로 올라갔을 때 고정
            if (tableRect.top <= headerHeight && tableRect.bottom > headerHeight) {
                setIsHeaderFixed(true);
            } else {
                setIsHeaderFixed(false);
            }
        };

        window.addEventListener('scroll', handleScroll, { passive: true });
        window.addEventListener('resize', handleScroll, { passive: true });
        handleScroll(); // 초기 상태 체크
        return () => {
            window.removeEventListener('scroll', handleScroll);
            window.removeEventListener('resize', handleScroll);
        };
    }, []);

    // 검색 필터링 함수 (모든 언어 포함)
    const handleSearch = useCallback((term) => {
        setSearchTerm(term);
        
        if (!term.trim()) {
            setFilteredData(fishesData);
            return;
        }
        
        const filtered = fishesData.filter(fish => {
            const searchLower = term.toLowerCase();
            return (
                fish.KR?.toLowerCase().includes(searchLower) ||
                fish.RU?.toLowerCase().includes(searchLower) ||
                fish.EN?.toLowerCase().includes(searchLower) ||
                fish.CN?.toLowerCase().includes(searchLower) ||
                fish.PL?.toLowerCase().includes(searchLower) ||
                fish.DE?.toLowerCase().includes(searchLower) ||
                fish.JP?.toLowerCase().includes(searchLower) ||
                fish.FR?.toLowerCase().includes(searchLower) ||
                fish.IT?.toLowerCase().includes(searchLower) ||
                fish.ES?.toLowerCase().includes(searchLower)
            );
        });
        setFilteredData(filtered);
    }, [fishesData]);

    useEffect(() => {
        const fetchData = async () => {
            try {
                // API URL 생성
                const response = await fetch(`/api/fishes`);
                if (!response.ok) throw new Error('Network response was not ok');
                
                const data = await response.json();
                setFishesData(data);
                setFilteredData(data);
                setLoading(false);
            } catch (error) {
                console.error('Error fetching data:', error);
                setLoading(false);
            }
        };

        fetchData();
    }, []);

    // 컬럼 매핑 메모이제이션
    const columnMapping = useMemo(() => ({
        KR: '한국어',
        RU: '러시아어',
        EN: '영어',
        CN: '중국어',
        PL: '폴란드어',
        DE: '독일어',
        JP: '일본어',
        FR: '프랑스어',
        IT: '이탈리아어',
        ES: '스페인어'
    }), []);

    // 표시할 컬럼 메모이제이션
    const displayColumns = useMemo(() => Object.keys(columnMapping), [columnMapping]);

    // 테이블 렌더링 메모이제이션
    const renderTable = useMemo(() => {
        if (filteredData.length === 0 && searchTerm) {
            return (
                <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
                    검색 결과가 없습니다.
                </div>
            );
        }
        
        if (filteredData.length === 0) return null;

        return (
            <>
                {/* 고정 헤더 (스크롤 시 표시) */}
                {isHeaderFixed && (
                    <div 
                        ref={cloneHeaderRef}
                        className="fixed-table-header"
                        style={{
                            position: 'fixed',
                            top: '65px',
                            left: '0',
                            right: '0',
                            zIndex: 99,
                            backgroundColor: 'var(--bg-primary, white)',
                            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                            padding: '0 20px'
                        }}
                    >
                        <table className="oil-table fish-table-fixed" style={{ 
                            marginBottom: 0,
                            width: '100%',
                            tableLayout: 'fixed'
                        }}>
                            <thead>
                                <tr>
                                    <th style={{ width: '70px' }}>이미지</th>
                                    {displayColumns.map(column => (
                                        <th key={column}>{columnMapping[column]}</th>
                                    ))}
                                </tr>
                            </thead>
                        </table>
                    </div>
                )}

                {/* 실제 테이블 */}
                <div className="table-wrapper">
                    <table ref={tableRef} className="oil-table fish-table-fixed" style={{
                        width: '100%',
                        tableLayout: 'fixed'
                    }}>
                        <thead ref={headerRef}>
                            <tr>
                                <th style={{ width: '70px' }}>이미지</th>
                                {displayColumns.map(column => (
                                    <th key={column}>{columnMapping[column]}</th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {filteredData.map((row, index) => (
                                <tr key={row.id || index}>
                                    <td style={{ width: '70px' }}>
                                        {row.filename && (
                                            <FishImage 
                                                filename={row.filename}
                                                alt={row.KR || row.filename}
                                                className="fish-image"
                                                onError={() => {
                                                    console.warn('Failed to load fish image:', row.filename);
                                                }}
                                            />
                                        )}
                                    </td>
                                    {displayColumns.map(column => (
                                        <td key={column} className="fish-name-cell">{row[column]}</td>
                                    ))}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </>
        );
    }, [filteredData, searchTerm, isHeaderFixed, displayColumns, columnMapping]);

    return (
        <>
        <h1 className="page-title">어종 번역</h1>
        <div>
            {loading ? (
                <div className="spinner-container">
                    <LoadingSpinner
                        height={80}
                        width={80}
                        color="#4fa94d"
                        visible={true}
                    />
                </div>
            ) : (
                <div className="page-content">
                    {/* 검색 입력 필드 */}
                    <div className="search-container" style={{ 
                        marginBottom: '20px',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        textAlign: 'center'
                    }}>
                        <input
                            type="text"
                            placeholder="모든 언어로 검색 (한국어, 영어, 러시아어, 중국어 등)"
                            value={searchTerm}
                            onChange={(e) => handleSearch(e.target.value)}
                            className="search-input"
                            style={{
                                width: '100%',
                                maxWidth: '500px',
                                padding: '12px 16px',
                                fontSize: '14px',
                                border: '1px solid #ddd',
                                borderRadius: '8px',
                                outline: 'none',
                                transition: 'border-color 0.3s ease'
                            }}
                            onFocus={(e) => e.target.style.borderColor = '#4fa94d'}
                            onBlur={(e) => e.target.style.borderColor = '#ddd'}
                        />
                        {searchTerm && (
                            <div style={{ marginTop: '8px', fontSize: '14px', color: '#666' }}>
                                {filteredData.length}개의 결과가 찾아졌습니다.
                            </div>
                        )}
                    </div>
                    
                    {/* 테이블 렌더링 */}
                    {renderTable}
                </div>
            )}
            
            {/* 댓글 시스템 */}
            <Comments pageName="fishes" />
        </div>
        </>
    );
};

export default Page1;